import { customElement, property, state } from 'lit/decorators.js'
import { css, CSSResultGroup, html, LitElement, nothing } from 'lit'
import { consume } from '@lit/context'

import { productTileIndexContext, productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { track } from 'components/shared/analytics/analytics.js'

import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'

initializeIconsByRole({
  'csc-plp-cta-button.view-product.icon': { name: 'angle-right', style: 'light' },
  'csc-plp-cta-button.configurator-product.icon': { name: 'sliders', style: 'light' },
  'csc-plp-cta-button.login-to-order.icon': { name: 'right-to-bracket', style: 'light' },
})

export const buttonSizes = ['default', 'small'] as const
export type buttonSizeTypes = typeof buttonSizes

export const fitVariants = ['auto', 'fill', 'square'] as const
export type fitVariantsTypes = typeof fitVariants

export const ctaButtonVariants = ['view-product', 'configurator-product', 'login-to-order', 'choose-variant'] as const
export type ctaButtonVariantTypes = typeof ctaButtonVariants

const style = css`
  :host {
    display: block;
    width: 100%;
  }

  :host([fit='auto']),
  :host([fit='square']) {
    width: fit-content;
  }
`

@customElement('csc-plp-cta-button')
export class CSCPlpCtaButton extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @property({ type: String })
  size: buttonSizeTypes[number] = 'default'

  @property({ type: String, reflect: true })
  fit: fitVariantsTypes[number] = 'fill'

  @property({ type: String })
  type: ctaButtonVariantTypes[number] = 'view-product'

  /** @deprecated Use `type` instead. */
  @property({ type: String })
  set variant(value: ctaButtonVariantTypes[number]) {
    console.warn('<csc-plp-cta-button> "variant" is deprecated, use "type" instead.')
    this.type = value
  }

  get variant() {
    return this.type
  }

  @consume({ context: productTileProductContext })
  @state()
  private _tileProduct: InternalProduct | undefined = undefined

  @consume({ context: productTileIndexContext, subscribe: true })
  @state()
  private _tileIndex: number | undefined = undefined

  private getLabel(): ReturnType<typeof t> {
    switch (this.type) {
      case 'configurator-product':
        return t('configurator_button_link', { ns: 'csc-product-tile', default: 'Configure product' })
      case 'login-to-order':
        return t('login_to_order_button_link', { ns: 'csc-product-tile', default: 'Login to order' })
      case 'choose-variant':
        return t('choose_variant_button_link', { ns: 'csc-product-tile', default: 'Choose your variant' })
      case 'view-product':
      default:
        return t('pdp_button_link', { ns: 'csc-product-tile', default: 'View product' })
    }
  }

  private openLoginSidebar() {
    const sidebarElement = document.querySelector('#login-sidebar')
    sidebarElement && sidebarElement.dispatchEvent(
      new CustomEvent('openSidebar', { bubbles: true, composed: true })
    )
  }

  private handleClick = () => {
    if (this.type === 'login-to-order') {
      this.openLoginSidebar()
      return
    }
    this.redirect()
  }

  redirect() {
    if (!this._tileProduct?.pageLink) return
    track({ type: 'product-selected', product: this._tileProduct, context: { ...(this._tileIndex !== undefined && { index: this._tileIndex }) } })
    this.windowLocation.redirect(
      new URL(this._tileProduct.pageLink, window.location.origin + window.location.pathname)
    )
  }

  override render() {
    return html`
      <cse-button variant="primary" fit="${this.fit}" size="${this.size}" class="pdp-link" @click=${this.handleClick}>
        ${this.getLabel()}
        ${this.type === 'choose-variant' ? nothing : html`<cse-icon icon-role="csc-plp-cta-button.${this.type}.icon"></cse-icon>`}
      </cse-button>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-plp-cta-button': CSCPlpCtaButton
  }
}
